import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

export enum InputSize {
  "x-small" = "py-1",
  small = "py-[7px]",
  medium = "py-[9px]",
  large = "py-[11px]",
}

export type TextInputV2Props = {
  label?: string;
  readOnly?: boolean;
  id?: string;
  value?: string;
  disabled?: boolean;
  name?: string;
  errorMessage?: string;
  dataTestId?: string;
  placeholder?: string;
  size?: keyof typeof InputSize;
  IconLeft?: React.ReactNode;
  IconRight?: React.ReactNode;
  iconContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  type?: "text" | "password" | "search";
  role?: React.HTMLAttributes<HTMLInputElement>["role"];
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  autoComplete?: string;
  className?: string;
  maxLength?: number;
};

export const TextInputV2 = React.forwardRef(
  (
    {
      label,
      readOnly,
      id = "text-field",
      disabled,
      errorMessage,
      name,
      dataTestId = "li-text-field-input",
      placeholder,
      size = "small",
      value,
      IconLeft,
      IconRight,
      iconContainerProps,
      type = "text",
      role,
      onChange,
      onInput,
      onKeyDown,
      onBlur,
      onFocus,
      autoComplete,
      className,
      maxLength,
    }: TextInputV2Props,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const [currentValue, setCurrentValue] = useState(value);
    const inputContainerClasses = twMerge(
      clsx(
        "group",
        "relative overflow-hidden",
        "flex items-center",
        "py-0",
        "bg-white border border-solid rounded-lg border-neutral-300 outline-transparent outline",
        "text-sm",
        {
          "bg-neutral-300 cursor-not-allowed text-neutral-400": disabled,
          "hover:border-neutral-500": !disabled,
          "hover:focus-within:border-platform-600": !disabled,
          "border-red-700": errorMessage,
          "focus-within:outline-hidden focus-within:border-platform-600": !errorMessage,
        }
      )
    );
    const inputClasses = clsx("leading-4 grow min-w-0 appearance-none focus:outline-none", InputSize[size], {
      "px-2": size === "x-small",
      "px-3": size !== "x-small",
      "cursor-not-allowed placeholder:text-neutral-400": disabled,
      "placeholder:text-neutral-300": !disabled,
    });

    useEffect(() => {
      setCurrentValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(e.target.value);
      onChange?.(e);
    };

    const handleOnBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(e.target.value);
      onBlur?.(e);
    };

    return (
      <>
        {label && (
          <label htmlFor={id} className="pb-2 m-0 text-xs font-semibold leading-5">
            {label}
          </label>
        )}
        <div className={className}>
          <div className={inputContainerClasses}>
            {IconLeft && (
              <div
                className={clsx("flex", {
                  "ml-2": size === "x-small",
                  "ml-3": size !== "x-small",
                })}
                {...iconContainerProps}
              >
                {IconLeft}
              </div>
            )}
            <input
              readOnly={readOnly}
              name={name}
              data-testid={dataTestId}
              id={id}
              disabled={disabled}
              type={type}
              role={role}
              className={inputClasses}
              placeholder={placeholder}
              value={currentValue}
              onChange={handleChange}
              onBlur={handleOnBlur}
              onInput={onInput}
              onKeyDown={onKeyDown}
              ref={ref}
              autoComplete={autoComplete ?? "off"}
              onFocus={onFocus}
              maxLength={maxLength}
            />
            {IconRight && (
              <div className={"mr-3 ml-1.5 flex"} {...iconContainerProps}>
                {IconRight}
              </div>
            )}
          </div>
          {errorMessage && (
            <p data-testid={`${dataTestId}-error`} className="pt-1 m-0 text-xs font-normal leading-5 text-red-700">
              {errorMessage}
            </p>
          )}
        </div>
      </>
    );
  }
);

TextInputV2.displayName = "TextInputV2";
